// src/components/DarkWebEnrollBusinessUnitModal.js
import React, { useState, useEffect } from "react";
import { X, Loader } from "lucide-react";
import { useApi } from "../hooks/useApi";

const DarkWebEnrollBusinessUnitModal = ({ isOpen, onClose, onEnrolled, existingSubscribedBUIds = [] }) => {
    const [businessUnits, setBusinessUnits] = useState([]);
    const [selectedBUId, setSelectedBUId] = useState("");
    const [domain, setDomain] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const apiCall = useApi();

    useEffect(() => {
        if (isOpen) {
            setSelectedBUId("");
            setDomain("");
            setError(null);
            fetchBusinessUnits();
        }
    }, [isOpen]);

    const fetchBusinessUnits = async () => {
        setLoading(true);
        try {
            const res = await apiCall("GET", `/businessUnits/all`);
            // Filter out BUs that already have a subscription
            const available = (res || []).filter(bu => !existingSubscribedBUIds.includes(bu.businessUnitId));
            setBusinessUnits(available);
        } catch (err) {
            setError("Failed to load business units");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!selectedBUId) return;

        setSaving(true);
        setError(null);
        try {
            const buDomain = domain.trim();

            // Always set DarkWeb category on the selected BU
            await apiCall("PUT", `/businessUnits/businessUnitUpdate-category/${selectedBUId}`, {
                businessUnitCategory: "DarkWeb",
                domain: buDomain || undefined
            });

            // Initialize Enzoic monitoring
            const selectedBU = businessUnits.find(bu => bu.businessUnitId.toString() === selectedBUId);
            const enrollDomain = buDomain || selectedBU?.domain;
            if (enrollDomain) {
                await apiCall("POST", `/enzoic/initialize-business-unit/${selectedBUId}`, { domain: enrollDomain });
            } else {
                setError("A domain is required to enable monitoring. Please enter a domain.");
                setSaving(false);
                return;
            }

            onEnrolled?.();
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Failed to enroll business unit");
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-6 border w-full max-w-lg shadow-lg rounded-md bg-white">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-content-primary">Enroll Business Unit for Live Monitoring</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="h-6 w-6" />
                    </button>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                        <p className="text-sm text-red-700">{error}</p>
                    </div>
                )}

                <form onSubmit={handleSave} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Business Unit</label>
                        <select
                            value={selectedBUId}
                            onChange={(e) => {
                                const id = e.target.value;
                                setSelectedBUId(id);
                                const bu = businessUnits.find(b => b.businessUnitId.toString() === id);
                                setDomain(bu?.domain || "");
                            }}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                            disabled={loading}
                        >
                            <option value="">-- Select a Business Unit --</option>
                            {businessUnits.map((bu) => (
                                <option key={bu.businessUnitId} value={bu.businessUnitId} title={bu.businessUnitName}>
                                    {bu.businessUnitName}
                                </option>
                            ))}
                        </select>
                        {!loading && businessUnits.length === 0 && (
                            <p className="text-sm text-gray-500 mt-2">
                                No business units available for enrollment.
                            </p>
                        )}
                    </div>

                    {selectedBUId && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
                            <input
                                type="text"
                                value={domain}
                                onChange={(e) => setDomain(e.target.value)}
                                placeholder="e.g., example.com"
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                            <p className="mt-1 text-sm text-gray-500">
                                Verify or update the domain for this business unit. A domain is required for live monitoring.
                            </p>
                        </div>
                    )}

                    <div className="flex justify-end space-x-3 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={!selectedBUId || saving}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? (
                                <>
                                    <Loader className="h-4 w-4 animate-spin" />
                                    Enrolling...
                                </>
                            ) : (
                                "Enroll"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DarkWebEnrollBusinessUnitModal;
